/**
 * Latitudes Online — Collection Filter Engine
 * Powers facets + product grid on /collections/all-artworks
 * from latitudes.online API, bypassing Shopify Search & Discovery.
 */
(function () {
  'use strict';

  if (!window.location.pathname.includes('/collections/all-artworks')) return;

  const API = 'https://latitudes.stockorder.co.za';
  const STOREFRONT_URL = 'https://latitudes-online.myshopify.com/api/2024-07/graphql.json';
  const STOREFRONT_TOKEN = 'f7871587cb5809fc2200ec1968f0bc0a';

  async function fetchShopifyAvailability(slugs) {
    if (!slugs.length) return {};
    const query = '{' + slugs.map((slug, idx) => `
      p_${idx}: product(handle: "${slug}") {
        availableForSale
      }
    `).join('\n') + '}';

    try {
      const res = await fetch(STOREFRONT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
        },
        body: JSON.stringify({ query })
      });
      const data = await res.json();
      const map = {};
      slugs.forEach((slug, idx) => {
        const p = data.data?.[`p_${idx}`];
        map[slug] = p ? p.availableForSale : true;
      });
      return map;
    } catch (e) {
      console.error('[latitudes-filters] Shopify availability error', e);
      const map = {};
      slugs.forEach(slug => { map[slug] = true; });
      return map;
    }
  }

  const CHEVRON_SVG = `<span class="m-accordion--item-icon">
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.5306 6.53073L8.5306 11.5307C8.46092 11.6007 8.37813 11.6561 8.28696 11.694C8.1958 11.7318 8.09806 11.7513 7.99935 11.7513C7.90064 11.7513 7.8029 11.7318 7.71173 11.694C7.62057 11.6561 7.53778 11.6007 7.4681 11.5307L2.4681 6.53073C2.3272 6.38984 2.24805 6.19874 2.24805 5.99948C2.24805 5.80023 2.3272 5.60913 2.4681 5.46823C2.60899 5.32734 2.80009 5.24818 2.99935 5.24818C3.19861 5.24818 3.3897 5.32734 3.5306 5.46823L7.99997 9.93761L12.4693 5.46761C12.6102 5.32671 12.8013 5.24756 13.0006 5.24756C13.1999 5.24756 13.391 5.32671 13.5318 5.46761C13.6727 5.60851 13.7519 5.7996 13.7519 5.99886C13.7519 6.19812 13.6727 6.38921 13.5318 6.53011L13.5306 6.53073Z" fill="currentColor"/>
    </svg>
  </span>`;

  const CHECK_ON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2.66699 0.333008H13.333C14.6217 0.333008 15.667 1.37833 15.667 2.66699V13.333C15.667 14.6217 14.6217 15.667 13.333 15.667H2.66699C1.37833 15.667 0.333008 14.6217 0.333008 13.333V2.66699C0.333008 1.37833 1.37833 0.333008 2.66699 0.333008Z" fill="#E9592B" stroke="#E9592B" stroke-width="0.666667"/>
    <path d="M4.5 8L7 10.5L11.5 6" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

  const CHECK_OFF = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="0.333333" y="0.333333" width="15.3333" height="15.3333" rx="2.33333" fill="white" stroke="black" stroke-width="0.666667"/>
  </svg>`;

  const state = {
    filters: {},
    sort: new URLSearchParams(window.location.search).get('sort_by') || 'created-descending',
    page: 1,
    totalPages: 1,
    collapsed: {},
    labels: {},   // {facetId: {value: displayLabel}} for pill rendering
  };

  let loadController  = null;
  let facetsController = null;
  let scrollObserver  = null;
  let gridLoading     = false;
  const rangeTimers   = {}; // per-dimension debounce timers (avoids cross-dim cancellation)

  // Update state.filters immediately from current number inputs, then debounce the API call.
  // Updating state first ensures any concurrent loadFacets re-render uses the latest values.
  function applyRange(dim) {
    const minEl  = document.getElementById(`lat-${dim}-min`);
    const maxEl  = document.getElementById(`lat-${dim}-max`);
    const minVal = (minEl?.value ?? '') !== '' ? parseFloat(minEl.value) : null;
    const maxVal = (maxEl?.value ?? '') !== '' ? parseFloat(maxEl.value) : null;
    if (minVal != null && !isNaN(minVal)) state.filters[dim + '_min'] = minVal;
    else delete state.filters[dim + '_min'];
    if (maxVal != null && !isNaN(maxVal)) state.filters[dim + '_max'] = maxVal;
    else delete state.filters[dim + '_max'];
    state.page = 1;

    clearTimeout(rangeTimers[dim]);
    rangeTimers[dim] = setTimeout(() => {
      load();
      loadFacets();
    }, 500);
  }

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  function esc(str) {
    return String(str ?? '').replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  function getCurrencySymbol() {
    const code = window.Shopify?.currency?.active || 'ZAR';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
        .formatToParts(0).find(p => p.type === 'currency')?.value ?? code;
    } catch { return code; }
  }

  function formatPrice(amount) {
    const currency = window.Shopify?.currency?.active || 'ZAR';
    try {
      return Number(amount).toLocaleString(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      return 'R\u00a0' + Number(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }

  function buildQuery() {
    const p = new URLSearchParams();
    p.set('sort_by', state.sort);
    p.set('page', state.page);
    Object.entries(state.filters).forEach(([key, val]) => {
      if (Array.isArray(val)) val.forEach(v => p.append(key + '[]', v));
      else if (val !== null && val !== '') p.set(key, val);
    });
    return p.toString();
  }

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  function setLoading(on) {
    const grid = document.querySelector('[data-product-container]');
    if (grid) grid.style.opacity = on ? '0.35' : '1';
  }

  // ---------------------------------------------------------------------------
  // Render: filter sidebar
  // ---------------------------------------------------------------------------

  function renderSidebar(facets) {
    const sidebar = document.querySelector('.m-sidebar');
    if (sidebar) sidebar.classList.remove('m:hidden');

    const container = document.querySelector('.m-filter--wrapper');
    if (!container) return;

    let html = '';

    const hasAny = Object.keys(state.filters).some(k => {
      const v = state.filters[k];
      return Array.isArray(v) ? v.length > 0 : v !== null && v !== '';
    });
    if (hasAny) {
      html += `<div style="padding:0 0 12px">
        <a href="#" id="lat-clear-all" style="font-size:.85em;color:#555;text-decoration:underline">Clear all filters</a>
      </div>`;
    }

    // Skip 'style' — not relevant for this collection
    const visible = facets.filter(f => f.id !== 'style');

    // Build label lookup so pills can show display names instead of raw values
    visible.forEach(facet => {
      if (facet.values) {
        state.labels[facet.id] = state.labels[facet.id] || {};
        facet.values.forEach(v => { state.labels[facet.id][v.value] = v.label; });
      }
      if (facet.label) state.labels[facet.id + '__label'] = facet.label;
    });

    const availabilityFacet = {
      id: 'availability',
      label: 'Availability',
      values: [
        { label: 'In stock', value: 'in_stock' },
        { label: 'Sold', value: 'sold' }
      ]
    };
    state.labels['availability'] = {
      'in_stock': 'In stock',
      'sold': 'Sold'
    };
    state.labels['availability__label'] = 'Availability';
    visible.unshift(availabilityFacet);

    visible.forEach((facet) => {
      const isOpen = !state.collapsed[facet.id];
      const contentStyle = isOpen ? 'opacity: 1;' : '';

      if (facet.type === 'price_range' || facet.type === 'dimension_range') {
        const minKey  = facet.id + '_min';
        const maxKey  = facet.id + '_max';
        const curMin  = state.filters[minKey] != null ? state.filters[minKey] : facet.min;
        const curMax  = state.filters[maxKey] != null ? state.filters[maxKey] : facet.max;
        const range   = facet.max - facet.min || 1;
        const fromPct = (((curMin - facet.min) / range) * 100).toFixed(1);
        const toPct   = (((curMax - facet.min) / range) * 100).toFixed(1);
        const symbol  = facet.type === 'price_range' ? getCurrencySymbol() : 'cm';
        html += `
          <div class="m-filter--widget m-accordion--item${isOpen ? ' open' : ''}" data-lat-facet="${facet.id}">
            <div class="m-filter--widget-title h5 m-accordion--item-button lat-acc-btn">
              <span>${esc(facet.label)}</span>
              ${CHEVRON_SVG}
            </div>
            <div class="m-filter--widget-content m-accordion--item-content" style="${contentStyle}">
              <div class="m-facets-price">
                <div class="m-facets-price--ranges"
                     style="--from:${fromPct}%;--to:${toPct}%;"
                     data-lat-range="${facet.id}"
                     data-range-min="${facet.min}" data-range-max="${facet.max}">
                  <input class="m-facets-price--range m-facets-price--range-min"
                         type="range" min="${facet.min}" max="${facet.max}" step="1"
                         value="${curMin}"
                         data-lat-slider-min="${facet.id}">
                  <input class="m-facets-price--range m-facets-price--range-max"
                         type="range" min="${facet.min}" max="${facet.max}" step="1"
                         value="${curMax}"
                         data-lat-slider-max="${facet.id}">
                </div>
                <div class="m-facets-price--input">
                  <div class="m-facets-price--field">
                    <label class="m-facets-price--label">From</label>
                    <span class="m-facets-price--field-currency">${symbol}</span>
                    <input type="number" id="lat-${facet.id}-min"
                           class="form-field form-field--input m:text-right"
                           placeholder="${Math.floor(facet.min)}"
                           value="${state.filters[minKey] != null ? curMin : ''}"
                           min="${facet.min}" max="${facet.max}" step="1"
                           data-lat-num-min="${facet.id}">
                  </div>
                  <span class="m-facets-price--to">–</span>
                  <div class="m-facets-price--field">
                    <label class="m-facets-price--label">To</label>
                    <span class="m-facets-price--field-currency">${symbol}</span>
                    <input type="number" id="lat-${facet.id}-max"
                           class="form-field form-field--input m:text-right"
                           placeholder="${Math.ceil(facet.max)}"
                           value="${state.filters[maxKey] != null ? curMax : ''}"
                           min="${facet.min}" max="${facet.max}" step="1"
                           data-lat-num-max="${facet.id}">
                  </div>
                </div>
              </div>
            </div>
          </div>`;
        return;
      }

      const active = state.filters[facet.id] || [];
      html += `
        <div class="m-filter--widget m-accordion--item${isOpen ? ' open' : ''}" data-lat-facet="${facet.id}">
          <div class="m-filter--widget-title h5 m-accordion--item-button lat-acc-btn">
            <span>${esc(facet.label)}${active.length ? ` <em style="font-weight:400;font-size:.85em">(${active.length})</em>` : ''}</span>
            ${CHEVRON_SVG}
          </div>
          <div class="m-filter--widget-content m-accordion--item-content" style="${contentStyle}">
            <ul class="m-facets m-filter--scroll-content m-scrollbar--vertical"
                style="--max-height:250px;list-style:none;margin:0;padding:0" role="list">`;

      facet.values.forEach((val, vi) => {
        const checked = active.includes(val.value);
        html += `
              <li class="m-facet--item">
                <label for="lat-f-${facet.id}-${vi}"
                       class="m-facet--checkbox${checked ? ' active remove-tag' : ''}">
                  <input type="checkbox"
                         id="lat-f-${facet.id}-${vi}"
                         class="m:visually-hidden"
                         data-lat-facet="${esc(facet.id)}"
                         value="${esc(val.value)}"
                         ${checked ? 'checked' : ''}>
                  ${checked ? CHECK_ON : CHECK_OFF}
                  <span class="m-facet--label">${esc(val.label)}</span>
                </label>
              </li>`;
      });

      html += `</ul>`;
      if (active.length) {
        html += `<a href="#" class="lat-clear-group" data-lat-facet="${facet.id}"
                    style="display:inline-block;margin-top:6px;font-size:.8em;color:#777;text-decoration:underline">Clear</a>`;
      }
      html += `</div></div>`;
    });

    container.innerHTML = html;

    bindSidebarEvents();
    renderActivePills();
  }

  // ---------------------------------------------------------------------------
  // Render: product grid
  // ---------------------------------------------------------------------------

  async function renderGrid(data, append = false) {
    const grid = document.querySelector('[data-product-container]');
    if (!grid) return;

    if (!data.products.length && !append) {
      grid.innerHTML = '<p style="grid-column:1/-1;padding:3rem;text-align:center;color:#666">No artworks found for the selected filters.</p>';
      renderCount(data);
      updateScrollSentinel(data);
      return;
    }

    const slugs = data.products.map(p => p.slug);
    const availabilityMap = await fetchShopifyAvailability(slugs);

    let productsToRender = data.products;
    const activeAvailability = state.filters.availability || [];
    if (activeAvailability.length > 0) {
      productsToRender = productsToRender.filter(p => {
        const isAvailable = availabilityMap[p.slug] !== false;
        if (activeAvailability.includes('in_stock') && isAvailable) return true;
        if (activeAvailability.includes('sold') && !isAvailable) return true;
        return false;
      });
    }

    if (!productsToRender.length && !append) {
      grid.innerHTML = '<p style="grid-column:1/-1;padding:3rem;text-align:center;color:#666">No artworks found for the selected filters.</p>';
      renderCount(data);
      updateScrollSentinel(data);
      return;
    }

    const html = productsToRender.map(p => {
      const isAvailable = availabilityMap[p.slug] !== false;
      const isSold = !isAvailable;
      const soldOutBadge = isSold ? `<span class="m-product-tag m-product-tag--soldout" style="z-index: 10 !important;">Sold</span>` : '';

      return `
        <div class="m-product-item m:w-6/12 md:m:w-4/12">
          <div class="m-product-card m-product-card--style-1" data-view="card" data-product-id="${p.id}">
            <div class="m-product-card__media">
              <a class="m-product-card__link m:block m:w-full" href="/products/${esc(p.slug)}">
                <div class="m-product-card__main-image">
                  <responsive-image class="m-image">
                    <img src="${esc(p.image)}" alt="${esc(p.title)}" loading="lazy" style="width:100%;height:auto;display:block">
                  </responsive-image>
                </div>
                ${soldOutBadge}
              </a>
            </div>
            <div class="m-product-card__content m:text-left">
              <div class="m-product-card__info">
                <span class="m-product-card__vendor">${esc(p.vendor)}</span>
                <h3 class="m-product-card__title">
                  <a class="m-product-card__name" href="/products/${esc(p.slug)}">${esc(p.title)}</a>
                </h3>
                <div class="m-product-card__price">
                  ${p.sale_price
                    ? `<span class="m-price--sale">${formatPrice(p.sale_price)}</span>
                       <span class="m-price--compare" style="text-decoration:line-through;color:#999;margin-left:6px">${formatPrice(p.price)}</span>`
                    : `<span class="m-price">${formatPrice(p.price)}</span>`}
                </div>
              </div>
            </div>
          </div>
        </div>`;
    }).join('');

    if (append) {
      grid.insertAdjacentHTML('beforeend', html);
    } else {
      grid.innerHTML = html;
    }

    renderCount(data);
    updateScrollSentinel(data);
  }

  function renderCount(data) {
    let pag = document.getElementById('lat-pagination');
    if (!pag) {
      pag = document.createElement('div');
      pag.id = 'lat-pagination';
      pag.style.cssText = 'width:100%;text-align:center;padding:1rem 0;color:#666;font-size:.9em';
      const grid = document.querySelector('[data-product-container]');
      grid?.parentNode?.insertBefore(pag, grid.nextSibling);
    }
    pag.textContent = data.total + ' artworks';
  }

  function updateScrollSentinel(data) {
    let sentinel = document.getElementById('lat-scroll-sentinel');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.id = 'lat-scroll-sentinel';
      sentinel.style.height = '1px';
      const pag = document.getElementById('lat-pagination');
      if (pag) pag.insertAdjacentElement('afterend', sentinel);
    }

    if (scrollObserver) scrollObserver.disconnect();

    if (!data || data.page >= data.total_pages) return;

    scrollObserver = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting || gridLoading) return;
      state.page++;
      load(true);
    }, { rootMargin: '300px' });

    scrollObserver.observe(sentinel);
  }

  // ---------------------------------------------------------------------------
  // Active filter pills
  // ---------------------------------------------------------------------------

  function renderActivePills() {
    const grid = document.querySelector('[data-product-container]');
    if (!grid) return;

    let el = document.getElementById('lat-active-filters');
    if (!el) {
      el = document.createElement('div');
      el.id = 'lat-active-filters';
      el.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;padding:8px 0 14px;';
      grid.parentNode.insertBefore(el, grid);
    }

    const pills = [];

    Object.entries(state.filters).forEach(([key, val]) => {
      if (key.endsWith('_min') || key.endsWith('_max')) return;
      if (!Array.isArray(val)) return;
      const facetLabels = state.labels[key] || {};
      val.forEach(v => pills.push({ key, value: v, label: facetLabels[v] || v }));
    });

    ['price', 'height', 'width'].forEach(dim => {
      const minVal = state.filters[dim + '_min'];
      const maxVal = state.filters[dim + '_max'];
      if (minVal == null && maxVal == null) return;
      const dimLabel = state.labels[dim + '__label'] || (dim.charAt(0).toUpperCase() + dim.slice(1));
      const symbol   = dim === 'price' ? getCurrencySymbol() + '\u00a0' : 'cm ';
      let txt = dimLabel + ': ';
      if (minVal != null && maxVal != null)  txt += `${symbol}${Math.round(minVal)} – ${symbol}${Math.round(maxVal)}`;
      else if (minVal != null)               txt += `from ${symbol}${Math.round(minVal)}`;
      else                                   txt += `up to ${symbol}${Math.round(maxVal)}`;
      pills.push({ label: txt, isRange: true, dim });
    });

    if (!pills.length) { el.innerHTML = ''; return; }

    const ps = 'display:inline-flex;align-items:center;gap:5px;padding:5px 12px;background:#fff;border:1px solid #ccc;border-radius:20px;font-size:.8em;cursor:pointer;white-space:nowrap;';
    el.innerHTML = pills.map((p, i) =>
      `<span class="lat-pill" data-pi="${i}" style="${ps}">${esc(p.label)}<span style="opacity:.5;font-size:1.1em;line-height:1">&times;</span></span>`
    ).join('');

    el.querySelectorAll('.lat-pill').forEach(pill => {
      pill.addEventListener('click', () => {
        const p = pills[parseInt(pill.dataset.pi, 10)];
        if (p.isRange) {
          delete state.filters[p.dim + '_min'];
          delete state.filters[p.dim + '_max'];
        } else {
          state.filters[p.key] = (state.filters[p.key] || []).filter(v => v !== p.value);
          if (!state.filters[p.key].length) delete state.filters[p.key];
        }
        state.page = 1;
        load();
        loadFacets();
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Sidebar events
  // ---------------------------------------------------------------------------

  function bindSidebarEvents() {
    document.querySelectorAll('input[data-lat-facet]').forEach(cb => {
      cb.addEventListener('change', () => {
        const id = cb.dataset.latFacet;
        if (!Array.isArray(state.filters[id])) state.filters[id] = [];
        if (cb.checked) {
          if (!state.filters[id].includes(cb.value)) state.filters[id].push(cb.value);
        } else {
          state.filters[id] = state.filters[id].filter(v => v !== cb.value);
          if (!state.filters[id].length) delete state.filters[id];
        }
        state.page = 1;
        load();
        loadFacets();
      });
    });

    document.querySelectorAll('.lat-clear-group').forEach(a => {
      a.addEventListener('click', e => {
        e.preventDefault();
        delete state.filters[a.dataset.latFacet];
        state.page = 1;
        load();
        loadFacets();
      });
    });

    document.getElementById('lat-clear-all')?.addEventListener('click', e => {
      e.preventDefault();
      state.filters = {};
      state.page = 1;
      load();
      loadFacets();
    });

    // Accordion toggle — stopImmediatePropagation blocks theme's delegated handler
    // so we get a single toggle instead of a double-toggle (which nets to nothing).
    // We also never re-init MinimogLibs.Accordion, which fixes the slider-height bug
    // (re-init calculates content height before range inputs paint → height=0).
    document.querySelectorAll('.lat-acc-btn').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopImmediatePropagation();
        const item    = btn.closest('.m-accordion--item');
        const content = item?.querySelector('.m-accordion--item-content');
        const facetId = item?.dataset.latFacet;
        const isOpen  = item?.classList.contains('open');
        item?.classList.toggle('open', !isOpen);
        if (content) content.style.opacity = isOpen ? '' : '1';
        if (facetId) state.collapsed[facetId] = isOpen;
      });
    });

    // Dual range sliders — sync slider ↔ number input, update --from/--to CSS vars
    document.querySelectorAll('[data-lat-slider-min]').forEach(slider => {
      const dim    = slider.dataset.latSliderMin;
      const track  = slider.closest('[data-lat-range]');
      const numMin = document.getElementById(`lat-${dim}-min`);
      const slMax  = track?.querySelector('[data-lat-slider-max]');
      const rMin   = parseFloat(track?.dataset.rangeMin ?? 0);
      const rMax   = parseFloat(track?.dataset.rangeMax ?? 100);

      slider.addEventListener('input', () => {
        const val = parseFloat(slider.value);
        if (slMax && val > parseFloat(slMax.value)) { slider.value = slMax.value; return; }
        if (numMin) numMin.value = Math.round(val);
        if (track) track.style.setProperty('--from', `${((val - rMin) / (rMax - rMin) * 100).toFixed(1)}%`);
        applyRange(dim);
      });
    });

    document.querySelectorAll('[data-lat-slider-max]').forEach(slider => {
      const dim    = slider.dataset.latSliderMax;
      const track  = slider.closest('[data-lat-range]');
      const numMax = document.getElementById(`lat-${dim}-max`);
      const slMin  = track?.querySelector('[data-lat-slider-min]');
      const rMin   = parseFloat(track?.dataset.rangeMin ?? 0);
      const rMax   = parseFloat(track?.dataset.rangeMax ?? 100);

      slider.addEventListener('input', () => {
        const val = parseFloat(slider.value);
        if (slMin && val < parseFloat(slMin.value)) { slider.value = slMin.value; return; }
        if (numMax) numMax.value = Math.round(val);
        if (track) track.style.setProperty('--to', `${((val - rMin) / (rMax - rMin) * 100).toFixed(1)}%`);
        applyRange(dim);
      });
    });

    // Number inputs sync back to sliders
    ['price', 'height', 'width'].forEach(dim => {
      const numMin = document.getElementById(`lat-${dim}-min`);
      const numMax = document.getElementById(`lat-${dim}-max`);
      const track  = document.querySelector(`[data-lat-range="${dim}"]`);
      const slMin  = track?.querySelector('[data-lat-slider-min]');
      const slMax  = track?.querySelector('[data-lat-slider-max]');
      const rMin   = parseFloat(track?.dataset.rangeMin ?? 0);
      const rMax   = parseFloat(track?.dataset.rangeMax ?? 100);

      numMin?.addEventListener('input', () => {
        const val = parseFloat(numMin.value);
        if (!isNaN(val) && slMin) {
          slMin.value = val;
          track?.style.setProperty('--from', `${((val - rMin) / (rMax - rMin) * 100).toFixed(1)}%`);
        }
        applyRange(dim);
      });
      numMax?.addEventListener('input', () => {
        const val = parseFloat(numMax.value);
        if (!isNaN(val) && slMax) {
          slMax.value = val;
          track?.style.setProperty('--to', `${((val - rMin) / (rMax - rMin) * 100).toFixed(1)}%`);
        }
        applyRange(dim);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------

  async function load(append = false) {
    if (gridLoading && append) return; // prevent stacking scroll loads
    if (!append) {
      // filter change — cancel any in-flight request and replace grid
      if (loadController) loadController.abort();
    }
    loadController = new AbortController();
    gridLoading = true;
    setLoading(!append);
    try {
      const res = await fetch(`${API}/api/products?${buildQuery()}`, { signal: loadController.signal });
      const data = await res.json();
      state.totalPages = data.total_pages;
      await renderGrid(data, append);
    } catch (e) {
      if (e.name !== 'AbortError') console.error('[latitudes-filters] products error', e);
    } finally {
      gridLoading = false;
      setLoading(false);
    }
  }

  async function loadFacets() {
    if (facetsController) facetsController.abort();
    facetsController = new AbortController();
    try {
      const res  = await fetch(`${API}/api/facets?${buildQuery()}`, { signal: facetsController.signal });
      const data = await res.json();
      renderSidebar(data.facets);
    } catch (e) {
      if (e.name !== 'AbortError') console.error('[latitudes-filters] facets error', e);
    }
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  async function init() {
    const sortSel = document.querySelector('select[name="sort_by"]');
    if (sortSel) {
      sortSel.value = state.sort;
      sortSel.addEventListener('change', () => {
        state.sort = sortSel.value;
        state.page = 1;
        load();
        loadFacets();
      });
      sortSel.closest('form')?.addEventListener('submit', e => e.preventDefault());
    }

    const nativeForm = document.getElementById('CollectionFiltersForm');
    if (nativeForm) {
      nativeForm.addEventListener('submit', e => e.preventDefault(), true);
    }

    // After Shopify re-renders the section for sort changes, restore our sidebar and grid.
    // renderPage() replaces the entire .facest-filters-section DOM, wiping our custom sidebar.
    // collection:rerendered fires once the replacement is done — we re-render on top of it.
    document.addEventListener('collection:rerendered', () => {
      state.sort = new URLSearchParams(window.location.search).get('sort_by') || state.sort;
      state.page = 1;
      loadFacets();
      load();
      // Re-kill the theme load-more that appears in the freshly rendered HTML
      document.querySelectorAll('[data-load-more]').forEach(btn => {
        (btn.closest('.m-load-more') || btn.parentNode)?.remove();
      });
    });

    // Kill theme's load-more button so its IntersectionObserver stops firing Shopify's unfiltered fetch.
    // We manage scroll-loading ourselves via updateScrollSentinel().
    document.querySelectorAll('[data-load-more]').forEach(btn => {
      (btn.closest('.m-load-more') || btn.parentNode)?.remove();
    });

    await Promise.all([loadFacets(), load()]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
